import type { CardChange, CardStackChangeSummary } from "@shared/lib/card-stack/change-summary"
import {
  countBy,
  describeChange,
  isEmptySummary,
  sumBy,
  summarizeCardStackChange,
} from "@shared/lib/card-stack/change-summary"
import { parseCardStackProseMirrorJson } from "@shared/lib/card-stack/prose-mirror-utils"
import type { FindFn } from "@shared/models/analytics/base"
import type { LessonAnalyticsCore } from "@shared/models/analytics/lesson"
import { LessonEditCard } from "@shared/models/analytics/lesson"
import type { ProseMirrorContent } from "@shared/models/card-stack"
import type { Node as PMNode } from "@tiptap/pm/model"
import { partial } from "lodash-es"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"
import { lessonAnalyticsCore } from "./lesson-analytics-core"
import { unifiedContentPatch } from "./unified-content-patch"

export interface Payload {
  cardStackId: string
  planbookId: string
  courseId: string | null
  dateString: string | null
  proseMirror: ProseMirrorContent
  originalProseMirror: ProseMirrorContent
  /** Who wrote this version: the user in the editor, or the AI panel's accepted edits. */
  source: "user" | "ai"
}

export function prepareLessonUnifiedUpdate(
  payload: Payload,
): PreparedAction & { narrative: NonNullable<PreparedAction["narrative"]> } {
  return {
    name: "LESSON_UNIFIED_UPDATE",
    payload: payload,
    patches: [unifiedContentPatch(payload.cardStackId, payload.proseMirror).patch],
    undoPatches: [unifiedContentPatch(payload.cardStackId, payload.originalProseMirror).patch],
    narrative: partial(narrative, payload),
  }
}

/**
 * The version's narrative: what changed in the cards between the last version boundary and now.
 *
 * Continuous typing is the one thing no label can state, so it is the only thing reported as an
 * event. Everything else the diff can see rides as context properties, so a fact a label already
 * stated is never counted twice.
 */
function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  const before = parseCardStackProseMirrorJson(payload.originalProseMirror.json)
  const after = parseCardStackProseMirrorJson(payload.proseMirror.json)
  const summary = summarizeCardStackChange(before, after)
  if (isEmptySummary(summary)) {
    // A formatting-only change, already documented by LESSON_EDITOR_OPERATION. Both lines are
    // still required: the version history paints the title, the undo menu the description.
    return {
      title: titles(payload.source).title,
      description: "Edited the lesson's formatting.",
    }
  }

  const models = defaultLessonModels(payload, findFn, userId)
  const analyticsCore = lessonAnalyticsCore(
    payload.cardStackId,
    payload.planbookId,
    models,
    findFn,
    userId,
  )
  const labeledElsewhere = googleClassroomCardIds(before, after)
  const events = summary.cards.flatMap((change) =>
    labeledElsewhere.has(change.cardId) ? [] : textEvents(change, analyticsCore),
  )

  return {
    ...titles(payload.source),
    description: describeChange(summary),
    context: { ...defaultLessonContext(payload, models), ...changeProperties(summary) },
    newAnalyticsEvents: events,
  }
}

/**
 * How the version names itself: an accepted AI edit reads as the AI's, in the version history
 * and in analytics alike, under the same names the legacy panel's LESSON_AI_CHANGE used.
 */
function titles(source: Payload["source"]): { title: string; titleForAnalytics: string } {
  if (source === "ai") {
    return { title: "Changed with AI", titleForAnalytics: "Lesson - AI Accept Changes" }
  }
  return { title: "Edited Lesson", titleForAnalytics: "Lesson - Edit Cards" }
}

/**
 * Words a card gained that nobody typed report nothing — the operation that put them there already
 * reported itself (`OP_ADD_CARD` for a new card's template text, `OP_MERGE_CARD_BACKWARD` for a
 * survivor's absorbed body).
 *
 * Only the *absorbed* words are discounted, never the whole card's: a card-menu delete removes a
 * card without merging, and the bystander it leaves must still report its own typing.
 */
function textEvents(change: CardChange, core: LessonAnalyticsCore): LessonEditCard[] {
  if (change.kind === "removed" || change.kind === "added") return []
  const events: LessonEditCard[] = []
  const bodyWordsTyped = change.bodyWordsAdded - change.bodyWordsAbsorbed
  if (bodyWordsTyped > 0 || change.bodyWordsRemoved > 0) {
    events.push(
      LessonEditCard.create(core, { verb: "editing", specifier: "value", cardId: change.cardId }),
    )
  }
  if (change.titleWordsAdded > 0 || change.titleWordsRemoved > 0) {
    events.push(
      LessonEditCard.create(core, { verb: "editing", specifier: "title", cardId: change.cardId }),
    )
  }
  return events
}

/**
 * Google Classroom cards are edited only through the sidebar, which commits each field with a
 * `OP_EDIT_GOOGLE_CLASSROOM_FIELD` label. Reporting their text again here would count one edit
 * twice, under two event types.
 */
function googleClassroomCardIds(before: PMNode, after: PMNode): Set<string> {
  const ids = new Set<string>()
  for (const doc of [before, after]) {
    doc.forEach((node) => {
      if (node.type.name === "cardGoogleClassroom") ids.add(node.attrs.id as string)
    })
  }
  return ids
}

function changeProperties(summary: CardStackChangeSummary): Record<string, unknown> {
  const cards = summary.cards
  return {
    cardsChanged: cards.length,
    cardsAdded: countBy(cards, (c) => c.kind === "added"),
    cardsRemoved: countBy(cards, (c) => c.kind === "removed"),
    cardsMoved: countBy(cards, (c) => c.kind === "moved"),
    cardTitleWordsAdded: sumBy(cards, (c) => c.titleWordsAdded),
    cardTitleWordsRemoved: sumBy(cards, (c) => c.titleWordsRemoved),
    cardBodyWordsAdded: sumBy(cards, (c) => c.bodyWordsAdded),
    cardBodyWordsRemoved: sumBy(cards, (c) => c.bodyWordsRemoved),
    attachmentsAdded: sumBy(cards, (c) => c.attachmentsAdded),
    attachmentsRemoved: sumBy(cards, (c) => c.attachmentsRemoved),
    standardsAdded: sumBy(cards, (c) => c.standardsAdded),
    standardsRemoved: sumBy(cards, (c) => c.standardsRemoved),
    linksAdded: sumBy(cards, (c) => c.linksAdded),
    linksRemoved: sumBy(cards, (c) => c.linksRemoved),
    cardColorsChanged: countBy(cards, (c) => c.colorChanged),
    cardVisibilitiesChanged: countBy(cards, (c) => c.isPublicChanged),
    cardChanges: cards,
  }
}
